import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Table } from 'primeng/table'
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, switchMap, filter, take } from 'rxjs/operators'
import { NewGroupModalComponent } from '../new-group-modal/new-group-modal.component'
import { EditGroupModalComponent } from '../edit-group-modal/edit-group-modal.component'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { AccountService } from '../../services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccountSubjectGroupDto, RoleDto, ApplicationDto } from 'src/app/services/dtos'
import {
  AccessLevelService,
  AccessLevel as BaseAccessLevel,
  AccountPermission,
} from 'src/app/services/access-level.service'
import { SidepanelService, SidepanelController } from '../../services/sidepanel.service'
import { DateFormattingService } from 'src/app/services/date-formatting.service'
import { handleArpioError } from 'src/utils/handle-error'

export interface UserInfo {
  name: string
  accountSubjectId: string
}

export interface GroupManagementItem {
  id: string
  name: string
  externalId?: string
  roles: { name: string; accessLevel: AccessLevel }[]
  assignedUsersCount: number
  lastModified: Date
  createdAt: Date
  assignedUsers: UserInfo[] // User info for the detail view (both direct and SSO)
  directUsers: UserInfo[] // Users directly assigned to the group
  ssoUsers: UserInfo[] // Users who have this group via SSO
}

export type AccessLevel = BaseAccessLevel | 'no_roles'

export interface PolicyInfo {
  accessLevel: string
  allApps: boolean
  appIds: string[] | null
  appNames?: string[] // For display purposes
  policyName?: string // Generated policy name for display
  inheritedFrom?: string[] // Role names that provide this policy
}

// Helper function to convert AccountSubjectGroupDto to GroupManagementItem
function convertGroupDto(group: AccountSubjectGroupDto, allRoles: any[], allUsers?: any[]): GroupManagementItem {
  // Load role details from the pre-loaded roles list
  const roleDetails: { name: string; accessLevel: AccessLevel }[] = []

  if (group.roles) {
    // Remove duplicates from roles array
    const uniqueRoleIds = [...new Set(group.roles)]

    for (const roleId of uniqueRoleIds) {
      // Find role in the pre-loaded roles list instead of making API call
      const roleDto = allRoles.find((role) => role.roleId === roleId)
      if (roleDto) {
        // Default access level
        const accessLevel = 'app_read'

        const roleDetail = {
          name: roleDto.name || '',
          accessLevel: accessLevel as AccessLevel,
        }

        roleDetails.push(roleDetail)
      } else {
        // If role not found in pre-loaded list, skip it
        console.warn(`Role ${roleId} not found in pre-loaded roles for group ${group.name}`)
      }
    }
  }

  // Sort role details alphabetically by name
  roleDetails.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  // Convert user IDs to user info using the provided user list
  const directUserInfos: UserInfo[] = []
  const ssoUserInfos: UserInfo[] = []

  if (allUsers) {
    // Get directly assigned users
    if (group.accountSubjects) {
      for (const userId of group.accountSubjects) {
        const user = allUsers.find((u) => u.accountSubjectId === userId)
        if (user) {
          directUserInfos.push({
            name: user.name || userId,
            accountSubjectId: user.accountSubjectId,
          })
        }
      }
    }

    // Get SSO users - users who have this group via lastSsoGroups
    const groupId = group.accountSubjectGroupId
    if (groupId) {
      for (const user of allUsers) {
        // Check if this user has the group via SSO and isn't already directly assigned
        if (user.lastSsoGroups?.includes(groupId) && !group.accountSubjects?.includes(user.accountSubjectId)) {
          ssoUserInfos.push({
            name: user.name || user.accountSubjectId,
            accountSubjectId: user.accountSubjectId,
          })
        }
      }
    }

    // Sort both arrays alphabetically by name
    directUserInfos.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    ssoUserInfos.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }

  // Combine direct and SSO users for the full list
  const allUserInfos = [...directUserInfos, ...ssoUserInfos]
  const totalUserCount = allUserInfos.length

  return {
    id: group.accountSubjectGroupId || '',
    name: group.name || '',
    externalId: group.externalId,
    roles: roleDetails,
    assignedUsersCount: totalUserCount,
    lastModified: group.lastUpdatedAt ? new Date(group.lastUpdatedAt) : new Date(),
    createdAt: group.createdAt ? new Date(group.createdAt) : new Date(),
    assignedUsers: allUserInfos,
    directUsers: directUserInfos,
    ssoUsers: ssoUserInfos,
  }
}

@Component({
  selector: 'app-group-management',
  templateUrl: './group-management.component.html',
  host: { class: 'rbac-management-page' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GroupManagementComponent implements OnInit, OnDestroy, SidepanelController {
  groups: GroupManagementItem[] = null
  isDataLoaded = false
  selectedGroup: GroupManagementItem | null = null
  selectedTableItem: GroupManagementItem | null = null // For table selection binding
  showSidePanel = false
  refreshingGroupIds: Set<string> = new Set()

  filterText = ''
  isFilterApplied = false
  loadingFilterInput = false
  debounceSubject = new BehaviorSubject<string>('')

  // User search functionality
  usersSearchText = ''
  filteredAssignedUsers: UserInfo[] = []

  // Delete functionality
  groupToDelete: GroupManagementItem = null

  @ViewChild('dt1') groupsTable: Table
  @ViewChild('filterInput') filterInput!: ElementRef
  @ViewChild('newGroupModal') newGroupModal: NewGroupModalComponent
  @ViewChild('editGroupModal') editGroupModal: EditGroupModalComponent
  @ViewChild('deleteGroupDialog', { static: true }) deleteGroupDialog: ModalDialogComponent
  @ViewChild('sidePanel') sidePanel: ElementRef

  // Permission properties
  canManageGroups$: Observable<boolean>
  canViewGroups$: Observable<boolean>
  unauthorizedMessage$: Observable<string>

  // Role and application data for policy calculations
  private allRoles: RoleDto[] = []
  private allApplications: ApplicationDto[] = []

  constructor(
    private router: Router,
    public accountService: AccountService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private uiService: UiService,
    private accessLevelService: AccessLevelService,
    private sidepanelService: SidepanelService,
    private dateFormattingService: DateFormattingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Initialize permission observables
    // canManageGroups requires both MANAGE_RBAC permission AND rbacFullEnabled
    this.canManageGroups$ = this.accessLevelService
      .hasAccountPermission(AccountPermission.MANAGE_RBAC)
      .pipe(map((hasPermission) => hasPermission && this.accountService.rbacFullEnabled))
    this.canViewGroups$ = this.accessLevelService.hasAccountPermission(AccountPermission.READ_ACCOUNT_SUBJECT_GROUP)
    this.unauthorizedMessage$ = of(
      this.accessLevelService.getUnauthorizedMessage(AccountPermission.READ_ACCOUNT_SUBJECT_GROUP)
    )

    // Redirect to account root if user doesn't have view permission
    this.canViewGroups$.subscribe((value) => {
      if (!value) {
        this.router.navigate([`/${this.accountService.accountId}`])
      }
    })

    this.setupDebounceFilter()
    this.checkForGroupSelection()

    // Wait for account to be loaded before loading data
    // This prevents the component from trying to load data before accountId is available
    this.accountService.accountIdStream.pipe(take(1)).subscribe(() => {
      this.loadGroupData()
    })

    // Register with sidepanel service
    this.sidepanelService.register(this, this)
  }

  private checkForGroupSelection() {
    this.route.queryParams.subscribe((params) => {
      const groupToSelect = params['selectGroup']
      if (groupToSelect) {
        this.selectGroupWhenLoaded(groupToSelect)
      }
    })
  }

  private selectGroupWhenLoaded(groupName: string) {
    if (this.groups) {
      const group = this.groups.find((g) => g.name === groupName)
      if (group) {
        this.selectedGroup = group
        this.selectedTableItem = group
        this.openSidePanel()
      }
    } else {
      // Retry after data loads
      setTimeout(() => this.selectGroupWhenLoaded(groupName), 100)
    }
  }

  ngOnDestroy() {
    // Cleanup subscriptions if any
    // Unregister from sidepanel service
    this.sidepanelService.unregister(this)
  }

  private setupDebounceFilter() {
    this.debounceSubject.pipe(debounceTime(600), distinctUntilChanged()).subscribe((filterText: string) => {
      this.applyFilter(filterText)
      this.loadingFilterInput = false
    })
  }

  private loadGroupData(): void {
    this.isDataLoaded = false
    // Wait for accountId to be available before making API calls
    this.accountService.accountIdStream
      .pipe(
        filter((accountId) => !!accountId),
        take(1),
        switchMap((accountId) =>
          forkJoin({
            allUsers: this.apiService.listAccountSubjects(accountId),
            allRoles: this.apiService.listRoles(accountId),
            groupDtos: this.apiService.listAccountSubjectGroups(accountId),
            allApplications: this.apiService.listApplications(accountId),
          })
        )
      )
      .subscribe({
        next: (data) => {
          // Store roles and applications for policy calculations
          this.allRoles = data.allRoles
          this.allApplications = data.allApplications

          // Convert groups using pre-loaded roles data (no individual API calls)
          const convertedGroups = data.groupDtos.map((dto) => convertGroupDto(dto, data.allRoles, data.allUsers))
          this.groups = convertedGroups.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

          // Clear refreshing indicators now that data is loaded
          this.refreshingGroupIds.clear()
          this.isDataLoaded = true
          this.cdr.detectChanges()
        },
        error: (error) => {
          console.error('❌ Failed to load groups:', error)
          this.groups = [] // Set empty array on error
          // Clear refreshing indicators even on error
          this.refreshingGroupIds.clear()
          this.isDataLoaded = true
          this.cdr.detectChanges()
        },
      })
  }

  onRowSelect(group: GroupManagementItem) {
    this.selectedGroup = group
    this.selectedTableItem = group
    this.openSidePanel()
  }

  private openSidePanel() {
    if (this.selectedGroup) {
      this.showSidePanel = true

      // Load/process data specific to this group when sidepanel opens
      this.usersSearchText = ''
      this.updateFilteredUsers()
    }
  }

  closeSidePanel() {
    this.showSidePanel = false
    this.selectedGroup = null
    this.selectedTableItem = null // Clear the table selection binding

    // Reset user search
    this.usersSearchText = ''
    this.filteredAssignedUsers = []
  }

  filterGlobal(event: any) {
    this.loadingFilterInput = true
    const filterText = event.target.value
    this.debounceSubject.next(filterText)
  }

  applyFilter(filterText: string) {
    if (!this.groupsTable || typeof filterText !== 'string') return

    const filterValue = filterText.toLowerCase().trim()

    if (!filterValue) {
      this.groupsTable.clear()
      this.filterText = filterValue
      this.isFilterApplied = false
      return
    }

    this.groupsTable.filterGlobal(filterValue, 'contains')
    this.filterText = filterValue
    this.isFilterApplied = true
  }

  clearInput() {
    this.filterText = ''
    this.filterInput.nativeElement.value = ''
    this.isFilterApplied = false
    this.groupsTable.clear()
  }

  // User search functionality
  filterAssignedUsers(event: any) {
    this.usersSearchText = event.target.value.toLowerCase().trim()
    this.updateFilteredUsers()
  }

  private updateFilteredUsers() {
    if (!this.selectedGroup?.assignedUsers) {
      this.filteredAssignedUsers = []
      return
    }

    if (!this.usersSearchText) {
      // Show all users by default
      this.filteredAssignedUsers = [...this.selectedGroup.assignedUsers]
    } else {
      // Filter users based on search text
      this.filteredAssignedUsers = this.selectedGroup.assignedUsers.filter((user) =>
        user.name.toLowerCase().includes(this.usersSearchText)
      )
    }
  }

  getFilteredAssignedUsers(): UserInfo[] {
    // If filteredAssignedUsers is empty but we have assigned users and no search text,
    // return all assigned users (handles initial display case)
    if (
      this.filteredAssignedUsers.length === 0 &&
      this.selectedGroup?.assignedUsers?.length > 0 &&
      !this.usersSearchText
    ) {
      return this.selectedGroup.assignedUsers
    }
    return this.filteredAssignedUsers
  }

  // Check if a user is an SSO user (not directly assigned)
  isUserSsoAssigned(userInfo: UserInfo): boolean {
    if (!this.selectedGroup) return false
    return this.selectedGroup.ssoUsers.some((u) => u.accountSubjectId === userInfo.accountSubjectId)
  }

  openNewGroupModal() {
    try {
      this.newGroupModal.open()
    } catch (error) {
      console.error('❌ Failed to open new group modal:', error)
    }
  }

  openEditGroupModal() {
    if (this.selectedGroup) {
      this.editGroupModal.open(this.selectedGroup)
    }
  }

  onGroupCreated() {
    // Refresh the groups data when a new group is created
    this.closeSidePanel()
    this.loadGroupData()
  }

  onGroupUpdated(group: GroupManagementItem) {
    // Add the group to the refreshing set to show spinner
    this.refreshingGroupIds.add(group.id)
    // Refresh the groups data when a group is updated or deleted
    this.closeSidePanel() // Close the side panel since the group might be deleted
    this.loadGroupData()
  }

  isGroupRefreshing(group: GroupManagementItem): boolean {
    return this.refreshingGroupIds.has(group.id)
  }

  groupTrackBy(_index: number, group: GroupManagementItem) {
    return group.id
  }

  getSortedGroupRoles(): { name: string; accessLevel: AccessLevel }[] {
    if (!this.selectedGroup) {
      return []
    }
    return this.sortRolesWithAccountRolesFirst(this.selectedGroup.roles)
  }

  getSortedGroupRolesLimited(
    group: GroupManagementItem,
    limit: number = 2
  ): { name: string; accessLevel: AccessLevel }[] {
    return this.sortRolesWithAccountRolesFirst(group.roles).slice(0, limit)
  }

  private sortRolesWithAccountRolesFirst(
    roles: { name: string; accessLevel: AccessLevel }[]
  ): { name: string; accessLevel: AccessLevel }[] {
    return roles.sort((a, b) => {
      // Account Administrator always first
      if (a.name === 'Account Administrator') return -1
      if (b.name === 'Account Administrator') return 1

      // Account Read-Only second
      if (a.name === 'Account Read-Only') return -1
      if (b.name === 'Account Read-Only') return 1

      // All other roles alphabetically
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    })
  }

  getRelativeTimeDisplay(date: Date): string {
    return this.dateFormattingService.getRelativeTimeDisplay(date, 'Never')
  }

  get globalFilterFields() {
    return ['name', 'roles', 'assignedUsersCount']
  }

  get rbacFullEnabled(): boolean {
    return this.accountService.rbacFullEnabled
  }

  getAccessLevelDisplay(accessLevel: AccessLevel | string): string {
    return this.accessLevelService.getAccessLevelDisplayName(accessLevel as any)
  }

  navigateToRole(roleName: string) {
    // Navigate to role management page with query parameters to select the role
    this.router.navigate([`/${this.accountService.accountId}/settings/role-management`], {
      queryParams: { selectRole: roleName },
    })
  }

  navigateToUser(accountSubjectId: string) {
    // Navigate to user management page with query parameters to select the user
    this.router.navigate([`/${this.accountService.accountId}/settings/user-management`], {
      queryParams: { selectUser: accountSubjectId },
    })
  }

  // Helper method to identify default account roles
  public isDefaultAccountRole(role: any): boolean {
    return this.accessLevelService.isDefaultAccountRole(role)
  }

  deleteGroup() {
    if (!this.selectedGroup) return

    this.groupToDelete = this.selectedGroup
    this.deleteGroupDialog.open()
  }

  confirmDeleteGroup() {
    if (!this.groupToDelete) return

    this.apiService.deleteAccountSubjectGroup(this.accountService.accountId, this.groupToDelete.id).subscribe(
      () => {
        this.deleteGroupDialog.close()
        this.closeSidePanel()
        this.loadGroupData() // Refresh the list
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  cancelDeleteGroup() {
    this.groupToDelete = null
    this.deleteGroupDialog.close()
  }

  // Get tooltip text for additional roles (roles beyond the first 2 displayed)
  getAdditionalRolesTooltip(group: GroupManagementItem): string {
    if (group.roles.length <= 2) return ''
    const additionalRoles = group.roles.slice(2)
    const badgeHtml = additionalRoles.map((role) => `<span class="tooltip-badge">${role.name}</span>`).join('')
    return `<div class="tooltip-badges-container">${badgeHtml}</div>`
  }

  // Policy-related methods for the group sidebar

  // Get all policies from roles assigned to the selected group, with deduplication
  getGroupPolicies(): PolicyInfo[] {
    if (!this.selectedGroup?.roles?.length) {
      return []
    }

    const policyMap = new Map<string, PolicyInfo>()

    // Process each role assigned to the group
    for (const groupRole of this.selectedGroup.roles) {
      // Find the full role details
      const fullRole = this.allRoles.find((role) => role.name === groupRole.name)
      if (!fullRole?.policies?.length) continue

      // Process each policy in the role
      for (const policy of fullRole.policies) {
        // Create a unique key for the policy based on access level and apps
        const policyKey = this.createPolicyKey(policy)

        if (policyMap.has(policyKey)) {
          // Policy already exists, add this role to inheritedFrom
          const existingPolicy = policyMap.get(policyKey)!
          if (!existingPolicy.inheritedFrom!.includes(groupRole.name)) {
            existingPolicy.inheritedFrom!.push(groupRole.name)
          }
        } else {
          // New policy, add it to the map
          policyMap.set(policyKey, {
            accessLevel: policy.accessLevel,
            allApps: policy.allApps || false,
            appIds: policy.appIds,
            appNames: this.getAppNamesFromIds(policy.appIds),
            inheritedFrom: [groupRole.name],
          })
        }
      }
    }

    // Convert map to array and add policy names
    const policies = Array.from(policyMap.values())
    policies.forEach((policy, index) => {
      policy.policyName = `Policy ${index + 1}`
    })

    return policies
  }

  // Create a unique key for policy deduplication
  private createPolicyKey(policy: any): string {
    const appIds = policy.appIds ? [...policy.appIds].sort().join(',') : 'no-apps'
    return `${policy.accessLevel}|${policy.allApps || false}|${appIds}`
  }

  // Convert app IDs to app names for display
  private getAppNamesFromIds(appIds: string[] | null): string[] {
    if (!appIds || appIds.length === 0) {
      return []
    }

    return appIds
      .map((id) => {
        const app = this.allApplications.find((app) => app.appId === id)
        return app?.name || id
      })
      .sort()
  }

  // Get policy description components for template rendering with tooltip support
  getPolicyDescriptionComponents(policy: PolicyInfo): {
    accessLevel: string
    prefix: string
    applicationsText: string
    suffix: string
    showTooltip: boolean
    tooltipText: string
  } {
    const accessDisplay = this.getAccessLevelDisplay(policy.accessLevel)

    if (policy.allApps) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access to ',
        applicationsText: 'all applications',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    if (!policy.appNames || policy.appNames.length === 0) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access',
        applicationsText: '',
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    if (policy.appNames.length === 1) {
      return {
        accessLevel: accessDisplay,
        prefix: ' access to ',
        applicationsText: policy.appNames[0],
        suffix: '',
        showTooltip: false,
        tooltipText: '',
      }
    }

    // Multiple applications - show tooltip
    return {
      accessLevel: accessDisplay,
      prefix: ' access to ',
      applicationsText: `${policy.appNames.length} applications`,
      suffix: '',
      showTooltip: true,
      tooltipText: `<div class="tooltip-badges-container">${policy.appNames.map((name) => `<span class="tooltip-badge">${name}</span>`).join('')}</div>`,
    }
  }

  // Format the inherited from roles for display
  getInheritedFromText(policy: PolicyInfo): string {
    if (!policy.inheritedFrom || policy.inheritedFrom.length === 0) {
      return ''
    }

    if (policy.inheritedFrom.length === 1) {
      return `Inherited from ${policy.inheritedFrom[0]} role`
    }

    return `Inherited from ${policy.inheritedFrom.join(', ')} roles`
  }
}
